import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Save, Cancel } from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";
import {
  TPremise,
  TPremiseStatus,
} from "../../../apis/spocPremise/spocPremise.interfaces";
import {
  useCreatePremiseHook,
  useUpdatePremiseHook,
} from "../../../hooks/useSpocHook";
import { logger } from "../../../utils/common";
import { useInstituteContext } from "../../../context/institute.context";

// Indian phone number validation: supports +91XXXXXXXXXX, 0XXXXXXXXXX, or XXXXXXXXXX (10 digits starting with 6-9)
const indianPhoneRegex = /^(\+91[6-9]\d{9}|0[6-9]\d{9}|[6-9]\d{9})$/;
// Indian postal code validation: 6 digits
const indianPostalCodeRegex = /^\d{6}$/;

const premiseSchema = z.object({
  name: z
    .string()
    .min(1, "Premise name is required")
    .min(2, "Premise name must be at least 2 characters")
    .max(100, "Premise name must not exceed 100 characters"),
  streetAddress: z
    .string()
    .min(1, "Street address is required")
    .min(5, "Street address must be at least 5 characters")
    .max(200, "Street address must not exceed 200 characters"),
  city: z
    .string()
    .min(1, "City is required")
    .min(2, "City must be at least 2 characters")
    .max(100, "City must not exceed 100 characters")
    .regex(/^[A-Za-z\s]+$/, "City should only contain letters and spaces"),
  state: z
    .string()
    .min(1, "State is required")
    .min(2, "State must be at least 2 characters")
    .max(100, "State must not exceed 100 characters")
    .regex(/^[A-Za-z\s]+$/, "State should only contain letters and spaces"),
  postalCode: z
    .string()
    .min(1, "Postal code is required")
    .regex(indianPostalCodeRegex, "Postal code must be exactly 6 digits"),
  phone: z
    .string()
    .min(1, "Phone number is required")
    .regex(indianPhoneRegex, "Please enter a valid Indian phone number"),
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  capacity: z
    .string()
    .min(1, "Capacity is required")
    .regex(/^\d+$/, "Capacity must be a positive number")
    .refine((val) => parseInt(val) > 0, "Capacity must be greater than 0"),
  status: z.enum(["active", "inactive", "archived"], {
    errorMap: () => ({ message: "Please select a valid status" }),
  }),
});

type PremiseFormData = z.infer<typeof premiseSchema>;

interface ManagePremiseDialogProps {
  open: boolean;
  onClose: () => void;
  premise?: TPremise | null;
}

// Helper function to parse address string into components
const parseAddress = (
  address: string,
): { street: string; city: string; state: string; postalCode: string } => {
  // Try to parse format like "Street, City - PostalCode" or "Street, City, State - PostalCode"
  const parts = address.split(" - ");
  const postalCode = parts.length > 1 ? parts[parts.length - 1].trim() : "";
  const locationParts = parts[0]?.split(",") || [];

  if (locationParts.length >= 3) {
    // Format: "Street, City, State"
    return {
      street: locationParts[0].trim(),
      city: locationParts[1].trim(),
      state: locationParts.slice(2).join(",").trim(),
      postalCode,
    };
  } else if (locationParts.length === 2) {
    // Format: "Street, City"
    return {
      street: locationParts[0].trim(),
      city: locationParts[1].trim(),
      state: "",
      postalCode,
    };
  }

  // Fallback: treat entire address as street
  return {
    street: address,
    city: "",
    state: "",
    postalCode,
  };
};

// Helper function to combine address components into string
const combineAddress = (
  street: string,
  city: string,
  state: string,
  postalCode: string,
): string => {
  const parts = [street];
  if (city) parts.push(city);
  if (state) parts.push(state);
  const address = parts.join(", ");
  return postalCode ? `${address} - ${postalCode}` : address;
};

const ManagePremiseDialog: FC<ManagePremiseDialogProps> = ({
  open,
  onClose,
  premise,
}) => {
  const { selectedInstitute } = useInstituteContext();
  const createPremiseMutation = useCreatePremiseHook();
  const updatePremiseMutation = useUpdatePremiseHook();
  const isEditMode = !!premise;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PremiseFormData>({
    resolver: zodResolver(premiseSchema),
    defaultValues: {
      name: "",
      streetAddress: "",
      city: "",
      state: "",
      postalCode: "",
      phone: "",
      email: "",
      capacity: "",
      status: "active",
    },
  });

  useEffect(() => {
    if (premise) {
      const parsedAddress = parseAddress(premise.address);
      reset({
        name: premise.name,
        streetAddress: parsedAddress.street,
        city: parsedAddress.city,
        state: parsedAddress.state,
        postalCode: parsedAddress.postalCode,
        phone: premise.phone,
        email: premise.email,
        capacity: premise.capacity.toString(),
        status: premise.status,
      });
    } else {
      reset({
        name: "",
        streetAddress: "",
        city: "",
        state: "",
        postalCode: "",
        phone: "",
        email: "",
        capacity: "",
        status: "active",
      });
    }
  }, [premise, reset, open]);

  const onSubmit = async (data: PremiseFormData) => {
    if (!selectedInstitute) {
      logger.show_error("Please select an institute first");
      return;
    }

    try {
      const address = combineAddress(
        data.streetAddress,
        data.city,
        data.state,
        data.postalCode,
      );

      if (isEditMode && premise) {
        await updatePremiseMutation.mutateAsync({
          id: premise.id,
          request: {
            name: data.name,
            address,
            phone: data.phone,
            email: data.email,
            capacity: parseInt(data.capacity),
            status: data.status,
          },
        });
        logger.show_success("Premise updated successfully!");
      } else {
        await createPremiseMutation.mutateAsync({
          instituteId: selectedInstitute.id,
          name: data.name,
          address,
          phone: data.phone,
          email: data.email,
          capacity: parseInt(data.capacity),
          status: data.status,
        });
        logger.show_success("Premise created successfully!");
      }
      onClose();
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : `Failed to ${isEditMode ? "update" : "create"} premise. Please try again.`,
      );
    }
  };

  const statusOptions: { key: TPremiseStatus; value: string }[] = [
    { key: "active", value: "Active" },
    { key: "inactive", value: "Inactive" },
    { key: "archived", value: "Archived" },
  ];

  const isLoading =
    isSubmitting ||
    createPremiseMutation.isPending ||
    updatePremiseMutation.isPending;
  const isError =
    createPremiseMutation.isError || updatePremiseMutation.isError;
  const errorMessage =
    createPremiseMutation.error?.message ||
    updatePremiseMutation.error?.message;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="manage-premise-dialog"
    >
      <DialogTitle data-testid="manage-premise-dialog-title">
        {isEditMode ? "Edit Premise" : "Add New Premise"}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)} data-testid="premise-form">
        <DialogContent data-testid="premise-form-content">
          {isError && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              data-testid="premise-form-error"
            >
              {errorMessage ||
                `Failed to ${isEditMode ? "update" : "create"} premise. Please try again.`}
            </Alert>
          )}

          <Grid
            container
            spacing={3}
            sx={{ mt: 1 }}
            data-testid="premise-form-container"
          >
            <ControlledTextField
              control={control as any}
              name="name"
              id="premise-name"
              label="Premise Name"
              required
              errorMessage={errors.name?.message}
            />

            <ControlledTextField
              control={control as any}
              name="streetAddress"
              id="street-address"
              label="Street Address"
              required
              multiline
              rows={2}
              errorMessage={errors.streetAddress?.message}
            />

            <ControlledTextField
              control={control as any}
              name="city"
              id="city"
              label="City"
              required
              errorMessage={errors.city?.message}
            />

            <ControlledTextField
              control={control as any}
              name="state"
              id="state"
              label="State"
              required
              errorMessage={errors.state?.message}
            />

            <ControlledTextField
              control={control as any}
              name="postalCode"
              id="postal-code"
              label="Postal Code"
              required
              errorMessage={errors.postalCode?.message}
              placeholder="6 digits (e.g., 110001)"
            />

            <ControlledTextField
              control={control as any}
              name="phone"
              id="premise-phone"
              label="Contact Phone"
              required
              errorMessage={errors.phone?.message}
            />

            <ControlledTextField
              control={control as any}
              name="email"
              id="premise-email"
              label="Email"
              type="email"
              required
              errorMessage={errors.email?.message}
            />

            <ControlledTextField
              control={control as any}
              name="capacity"
              id="premise-capacity"
              label="Capacity"
              required
              type="number"
              errorMessage={errors.capacity?.message}
              placeholder="Number of students/staff"
            />

            <ControlledSelectField
              control={control as any}
              name="status"
              id="premise-status"
              label="Status"
              itemList={statusOptions}
              isRequired
              errorMessage={errors.status?.message}
            />
          </Grid>
        </DialogContent>
        <DialogActions data-testid="premise-dialog-actions">
          <Button
            onClick={onClose}
            disabled={isLoading}
            startIcon={<Cancel />}
            data-testid="cancel-premise-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
            data-testid="save-premise-button"
          >
            {isLoading
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update Premise"
                : "Create Premise"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManagePremiseDialog;
